import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react'
import { registerToastHandler } from '../lib/toastBus'

type ToastType = 'success' | 'error' | 'info'

interface Toast {
  id: number
  message: string
  type: ToastType
  action?: { label: string; onClick: () => void }
}

interface ToastContextValue {
  addToast: (message: string, type?: ToastType, action?: { label: string; onClick: () => void }) => void
}

const ToastContext = createContext<ToastContextValue>({ addToast: () => {} })

export const useToast = () => useContext(ToastContext)

let nextId = 0

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const addToast = useCallback((message: string, type: ToastType = 'success', action?: { label: string; onClick: () => void }) => {
    const id = nextId++
    setToasts((prev) => [...prev, { id, message, type, action }])
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, 4000)
  }, [])

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  // Bridge non-React callers (axios interceptor) to this provider.
  useEffect(() => {
    registerToastHandler((message, type) => addToast(message, type))
    return () => registerToastHandler(null)
  }, [addToast])

  const colors = {
    success: { bg: 'var(--color-success-bg)', border: 'var(--color-success)', text: 'var(--color-success)' },
    error: { bg: 'var(--color-error-bg)', border: 'var(--color-error)', text: 'var(--color-error)' },
    info: { bg: 'var(--color-info-bg)', border: 'var(--color-info)', text: 'var(--color-info)' },
  }

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      <div style={{
        position: 'fixed', top: '20px', right: '20px',
        zIndex: 10000, display: 'flex', flexDirection: 'column', gap: '8px',
      }}>
        {toasts.map((t) => (
          <div key={t.id} className="font-body" style={{
            padding: '12px 20px', borderRadius: '12px',
            background: colors[t.type].bg,
            border: `1px solid ${colors[t.type].border}`,
            color: colors[t.type].text,
            fontSize: '14px', fontWeight: 500,
            animation: 'slideIn 0.3s ease-out',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            maxWidth: '400px',
            display: 'flex', alignItems: 'center', gap: '12px',
          }}>
            <span style={{ flex: 1 }}>{t.message}</span>
            {t.action ? (
              <button
                type="button"
                onClick={() => { t.action!.onClick(); dismiss(t.id) }}
                style={{
                  border: `1px solid ${colors[t.type].text}`,
                  background: 'transparent',
                  color: colors[t.type].text,
                  borderRadius: '6px',
                  padding: '4px 10px',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {t.action.label}
              </button>
            ) : null}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}
